import OrderDetailsView from "./OrderDetailsView";
import {useParams} from "react-router-dom";
import {useEffect, useState} from "react";
import {getOrderById, getUserOrders, leaveOrderComment} from "../../repository/orders";
import {useToast} from "@chakra-ui/react";
import {useTranslation} from "react-i18next";
import {useAppDispatch, useAppSelector} from "../../hooks";
import {RootState} from "../../store";
import {fetchAllLocations} from "../../reducers/locations";

export default () => {
    const params = useParams();
    const [order, setOrder] = useState(null as null | any);
    const [userOrders, setUserOrders] = useState([] as any[]);
    const [isFirstOrder, setIsFirstOrder] = useState(true);
    const [hasValidPlaceId, setHasValidPlaceId] = useState(false);
    const toast = useToast();
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const locationsState = useAppSelector((state: RootState) => state.locations);

    useEffect(() => {
        if(params.id) {
            getOrderById(params.id).then(data => {
                setOrder(data.data)
            }).catch(err => {
                toast({
                    title: t('orderFetch'),
                    description:
                        t('orderError'),
                    status: 'error',
                    duration: 9000,
                    isClosable: true,
                })
            })

            // Get user's order history to check if this is their first order
            getUserOrders().then(response => {
                const orders = response.data?.orders || [];
                setUserOrders(orders);
                setIsFirstOrder(orders.length <= 1); // If there's only this order or no orders, it's the first order
            }).catch(err => {
                console.error("Failed to fetch user orders:", err);
            });

            // Fetch locations if they're not already in the Redux state
            if (!locationsState.data.length) {
                dispatch(fetchAllLocations());
            }
        }
    }, [params, dispatch])

    // Check if there's a valid placeId available
    useEffect(() => {
        if (order?.locationId && locationsState.data.length) {
            // Try to get placeId from the selected location
            let placeId = locationsState.selectedLocation?.placeId;

            // If not available, try to find the location using the order's locationId
            if (!placeId && order.locationId) {
                const location = locationsState.data.find(loc => loc.locationId === order.locationId);
                placeId = location?.placeId;
            }

            // Check if placeId is valid (not null, undefined, or empty string)
            setHasValidPlaceId(!!placeId && placeId.trim() !== '');
        } else {
            setHasValidPlaceId(false);
        }
    }, [order, locationsState.data, locationsState.selectedLocation])

    const handleLeaveComment = (rating: number, comment: string) => {
        // If it's not the first order and we have a valid placeId, redirect to Google Places
        if (!isFirstOrder && hasValidPlaceId) {
            // First try to get placeId from the selected location
            let placeId = locationsState.selectedLocation?.placeId;

            // If not available, try to find the location using the order's locationId
            if (!placeId && order?.locationId) {
                const location = locationsState.data.find(loc => loc.locationId === order.locationId);
                placeId = location?.placeId;
            }

            // If we have a placeId, redirect to Google Places
            if (placeId && placeId.trim() !== '') {
                // Open Google Places review in a new tab
                window.open(`https://search.google.com/local/writereview?placeid=${placeId}`, '_blank');
            }
        }

        leaveOrderComment(order.orderId, rating, comment).then(data => {
            toast({
                title: t('orderComment'),
                description:
                    t('orderCommentSuccess'),
                status: 'success',
                duration: 9000,
                isClosable: true,
            })
        }).catch(() =>{
            toast({
                title: t('orderComment'),
                description:
                    t('orderCommentFail'),
                status: 'error',
                duration: 9000,
                isClosable: true,
            })
        })
    }

    return <OrderDetailsView order={order} handleLeaveComment={handleLeaveComment} isFirstOrder={isFirstOrder} hasValidPlaceId={hasValidPlaceId}/>
}
